@file:OptIn(ExperimentalFoundationApi::class, kotlinx.coroutines.FlowPreview::class)

package org.teddapp.project.ui.admin

import androidx.compose.foundation.ExperimentalFoundationApi
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.text.input.TextFieldState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.runtime.snapshotFlow
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.flow.debounce
import kotlinx.coroutines.flow.drop
import kotlinx.coroutines.launch
import kotlinx.serialization.json.JsonObject
import org.teddapp.project.server.Draft
import org.teddapp.project.server.Presentation
import org.teddapp.project.server.ServerPublication
import org.teddapp.project.server.ServerSeries
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddConfirmDialog
import org.teddapp.project.ui.TeddField
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddSpace
import org.teddapp.project.ui.TeddTab
import org.teddapp.project.ui.TeddTabs
import org.teddapp.project.ui.TeddTag
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType
import org.teddapp.project.ui.TeddVisibility
import org.teddapp.project.ui.TeddVisibilityBadge
import org.teddapp.project.ui.notes.ListContinuation
import org.teddapp.project.ui.notes.MarkdownEdits
import org.teddapp.project.ui.notes.MdEdit
import org.teddapp.project.ui.notes.NoteFormatBar
import org.teddapp.project.ui.notes.format
import org.teddapp.project.ui.notes.markdownShortcuts
import org.teddapp.project.ui.projects.MenuAction
import org.teddapp.project.ui.projects.ProjectMoreMenu
import org.teddapp.project.ui.teddClickable
import org.teddapp.project.ui.teddSurface

// ──────────────────────────────────────────────────────────────────────
// L'éditeur : écrire un article, une pièce de portfolio ou une page directement
// dans le back-office, sans passer par une note. Une colonne de lecture au
// milieu (couverture, titre, chapeau, texte en Markdown avec la barre des Notes
// et un menu « / »), un panneau à droite (adresse, classement, autre langue,
// référencement, liste de contrôle). Un brouillon s'enregistre tout seul ; un
// contenu en ligne attend qu'on publie ses modifications.
// ──────────────────────────────────────────────────────────────────────

/** Ce qu'on écrit, tel qu'on l'écrit. Tout est état Compose : l'écran suit la frappe. */
internal class EditorState(
    /** Absent tant que le serveur n'a pas reçu le premier enregistrement. */
    id: String?,
    /** `note`, `portfolio_piece` ou `page`. */
    val kind: String,
    val lang: String,
    /** Pour une page : `home`, `about` ou rien. */
    val role: String?,
    /** La version dans l'autre langue, quand on traduit. */
    val translationOf: String?,
    val known: List<KnownSeries>,
    initialTitle: String = "",
    initialBody: String = "",
    online: Boolean = false,
) {
    var id by mutableStateOf(id)
    var title by mutableStateOf(initialTitle)
    var slug by mutableStateOf("")
    var slugEdited by mutableStateOf(false)
    var subtitle by mutableStateOf("")
    var summary by mutableStateOf("")
    val body = TextFieldState(initialBody)
    var tags by mutableStateOf("")
    var cover by mutableStateOf("")
    var coverAlt by mutableStateOf("")
    var seoTitle by mutableStateOf("")
    var seoDescription by mutableStateOf("")
    var seriesKey by mutableStateOf<String?>(null)
    var seriesTitle by mutableStateOf("")
    var episode by mutableStateOf("")
    var dateStart by mutableStateOf("")
    var dateEnd by mutableStateOf("")
    var links by mutableStateOf("")
    val images = mutableStateListOf<Pair<String, String>>()
    var stats by mutableStateOf("")
    val home = HomeState(org.teddapp.project.server.HomeExtras())
    val about = AboutState(org.teddapp.project.server.AboutExtras())

    /** En ligne sur le site. */
    var online by mutableStateOf(online)
    var savedAtMs by mutableStateOf<Long?>(null)
    /** Ce qui a été enregistré en dernier : différent de [fingerprint], il y a du nouveau. */
    var savedFingerprint by mutableStateOf<String?>(null)
    var saving by mutableStateOf(false)
    var error by mutableStateOf<String?>(null)
    var confirmDelete by mutableStateOf(false)
    /** Le dialogue Publier est ouvert : on relit ce qui part avant de confirmer. */
    var confirmPublish by mutableStateOf(false)
    var confirmWithdraw by mutableStateOf(false)

    val isPage get() = kind == "page"
    val isArticle get() = kind == "note"
    val isPiece get() = kind == "portfolio_piece"

    /** L'adresse telle qu'elle sera sur le site. */
    val path: String
        get() = when {
            isArticle -> "/$lang/blog/${slugify(slug)}"
            isPiece -> "/$lang/portfolio/${slugify(slug)}"
            role == "home" -> "/$lang/"
            role == "about" -> "/$lang/" + if (lang == "en") "about/" else "a-propos/"
            else -> "/$lang/${slugify(slug)}"
        }

    val words: Int get() = body.text.split(Regex("\\s+")).count { it.isNotBlank() }
    val readingMinutes: Int get() = (words / 220).coerceAtLeast(1)

    private val inSeries get() = seriesKey != null
    private fun seriesKeyValue() = if (seriesKey == ArticleState.NEW_SERIES) slugify(seriesTitle) else seriesKey.orEmpty()
    val seriesValid: Boolean
        get() = !inSeries || (seriesTitle.isNotBlank() && episode.toIntOrNull() in 1..99 && seriesKeyValue().isNotEmpty())
    fun series(): ServerSeries? = if (inSeries && seriesValid) ServerSeries(seriesKeyValue(), seriesTitle.trim(), episode.toIntOrNull() ?: 1) else null

    fun tagList() = tags.split(',', '\n').map { it.trim().removePrefix("#") }.filter { it.isNotEmpty() }.take(12)
    fun linkList() = links.lines().mapNotNull { line ->
        val url = line.substringAfterLast('|').trim()
        val label = if ('|' in line) line.substringBeforeLast('|').trim() else ""
        if (url.isEmpty()) null else label.ifEmpty { url.removePrefix("https://").removePrefix("http://").substringBefore('/') } to url
    }.take(8)

    /** Tout ce qui compte pour l'enregistrement, en une chaîne : change, on enregistre. */
    fun fingerprint(): String = listOf(
        title, slug, subtitle, summary, body.text.toString(), tags, cover, coverAlt, seoTitle, seoDescription,
        seriesKey.orEmpty(), seriesTitle, episode, dateStart, dateEnd, links, images.joinToString("|") { it.first + "=" + it.second },
        stats, home.appLead, home.appPoints, home.ctaTitle, home.ctaBody, home.ctaButton, about.email, about.phone, about.links,
    ).joinToString("\u0000")

    val dirty: Boolean get() = savedFingerprint != fingerprint()
    val ready: Boolean get() = title.isNotBlank() && slugify(slug).isNotEmpty() && seriesValid

    fun presentation() = Presentation(cover.trim(), coverAlt.trim(), seoTitle.trim(), seoDescription.trim())

    fun draft(published: Boolean) = Draft(
        id = id, kind = kind, slug = slugify(slug), lang = lang, title = title.trim(), body = body.text.toString(),
        published = published, translationOf = translationOf,
        subtitle = subtitle.trim(), summary = summary.trim(), tags = tagList(), series = series(),
        dateStart = dateStart.trim(), dateEnd = dateEnd.trim(), links = linkList(), images = images.filter { it.first.isNotBlank() },
        presentation = presentation(),
    )

    /** La liste de contrôle : ce qui est prêt, ce qui manque, ce qui est facultatif. */
    fun checklist(): List<Triple<String, CheckState, String?>> = buildList {
        add(Triple(if (isPiece) "Titre et sous-titre" else "Titre et chapeau", if (title.isNotBlank() && (isPage || subtitle.isNotBlank())) CheckState.Done else if (title.isBlank()) CheckState.Missing else CheckState.Optional, null))
        add(Triple("Un texte", if (words > 0) CheckState.Done else CheckState.Missing, null))
        add(Triple("Une adresse sur le site", if (slugify(slug).isNotEmpty()) CheckState.Done else CheckState.Missing, null))
        if (!isPage) add(Triple("Couverture et son texte alternatif", when { cover.isBlank() -> CheckState.Optional; coverAlt.isBlank() -> CheckState.Warn; else -> CheckState.Done }, if (cover.isNotBlank() && coverAlt.isBlank()) "Décris l'image" else null))
        if (isArticle) add(Triple("Un résumé pour le catalogue", if (summary.isNotBlank()) CheckState.Done else CheckState.Warn, null))
        add(Triple("Description pour les moteurs", if (seoDescription.isNotBlank() || summary.isNotBlank()) CheckState.Done else CheckState.Warn, null))
        add(Triple("Version " + if (lang == "en") "française" else "anglaise", CheckState.Optional, null))
    }

    fun previewDoc() = PreviewDoc(
        kind = kind, lang = lang, slug = slugify(slug).ifEmpty { "adresse" }, title = title, body = body.text.toString(),
        subtitle = subtitle, summary = summary, cover = cover.trim(), images = images.toList(), links = linkList(),
        stats = if (isPage) parseStats(stats) else emptyList(), tags = tagList(), series = series(), role = role,
        publishedAtMs = savedAtMs ?: 0, online = online, draft = true,
    )

    /** Une copie à traduire : même contenu, autre langue, pas encore enregistrée, reliée à [translationOf]. */
    fun copyForLang(lang: String, translationOf: String): EditorState {
        val from = this
        return EditorState(id = null, kind = kind, lang = lang, role = role, translationOf = translationOf, known = known, initialTitle = from.title, initialBody = from.body.text.toString()).apply {
            slug = when (role) { "home" -> if (lang == "en") "home" else "accueil"; "about" -> if (lang == "en") "about" else "a-propos"; else -> slugify(from.title) }
            slugEdited = role != null
            subtitle = from.subtitle; summary = from.summary; tags = from.tags; cover = from.cover; coverAlt = from.coverAlt
            seriesKey = from.seriesKey?.takeIf { it != ArticleState.NEW_SERIES }; episode = from.episode
            seriesTitle = known.firstOrNull { it.key == seriesKey }?.titles?.get(lang).orEmpty()
            dateStart = from.dateStart; dateEnd = from.dateEnd; links = from.links; images.addAll(from.images); stats = from.stats
            home.appLead = from.home.appLead; home.appPoints = from.home.appPoints; home.ctaTitle = from.home.ctaTitle; home.ctaBody = from.home.ctaBody; home.ctaButton = from.home.ctaButton
            about.email = from.about.email; about.phone = from.about.phone; about.links = from.about.links
        }
    }

    companion object {
        /** L'état d'une publication relue sur le serveur, avec son contenu. */
        fun of(p: ServerPublication, known: List<KnownSeries>): EditorState {
            val c = p.content ?: JsonObject(emptyMap())
            val series = p.series ?: (c["series"] as? JsonObject)?.let { s ->
                s.text("key").takeIf { it.isNotEmpty() }?.let { ServerSeries(it, s.text("title"), s.text("episode").toIntOrNull() ?: 1) }
            }
            return EditorState(
                id = p.id, kind = p.source_kind, lang = p.lang, role = p.role ?: c.text("role").ifBlank { null },
                translationOf = null, known = known, initialTitle = p.title, initialBody = c.text("body"), online = p.online,
            ).apply {
                slug = p.slug; slugEdited = true
                subtitle = c.text("subtitle"); summary = c.text("summary")
                tags = c.strings("tags").joinToString(", ")
                cover = c.text("cover"); coverAlt = c.text("cover_alt")
                seoTitle = c.text("seo_title"); seoDescription = c.text("seo_description")
                seriesKey = series?.key; seriesTitle = series?.title.orEmpty(); episode = series?.episode?.toString().orEmpty()
                dateStart = c.text("date_start"); dateEnd = c.text("date_end")
                links = c.objects("links").map { it.text("label") to it.text("url") }.filter { it.second.isNotBlank() }.joinToString("\n") { "${it.first} | ${it.second}" }
                images.addAll(c.objects("images").map { it.text("url") to it.text("caption") }.filter { it.first.isNotBlank() })
                stats = statLines(p)
                homeExtrasOf(p).let { h -> home.appLead = h.app_lead; home.appPoints = h.app_points.joinToString("\n"); home.ctaTitle = h.cta_title; home.ctaBody = h.cta_body; home.ctaButton = h.cta_button }
                aboutExtrasOf(p).let { a -> about.email = a.email; about.phone = a.phone; about.links = a.links.joinToString("\n") { "${it.label} | ${it.url}" } }
                savedFingerprint = fingerprint()
                savedAtMs = p.published_at_ms
            }
        }
    }
}

internal enum class CheckState { Done, Warn, Missing, Optional }

/** Un bloc que le menu « / » insère : son nom, une aide, et le Markdown posé. */
private class SlashItem(val code: String, val label: String, val insert: String, val cursorBack: Int = 0)

private val SLASH_ITEMS = listOf(
    SlashItem("H2", "Titre de section", "## "),
    SlashItem("H3", "Sous-titre", "### "),
    SlashItem("•", "Liste à puces", "- "),
    SlashItem("1.", "Liste numérotée", "1. "),
    SlashItem("«", "Citation", "> "),
    SlashItem("</>", "Bloc de code", "```\n\n```", cursorBack = 4),
    SlashItem("!", "Encadré", "> **À noter** · "),
    SlashItem("—", "Séparateur", "---\n"),
)

/** Ce qu'on tape après un « / » seul en début de ligne : le menu filtre dessus. Null sans « / ». */
private fun slashQuery(state: TextFieldState): Pair<Int, String>? {
    val sel = state.selection
    if (!sel.collapsed) return null
    val text = state.text
    val cursor = sel.start
    val lineStart = text.lastIndexOf('\n', cursor - 1) + 1
    val line = text.subSequence(lineStart, cursor).toString()
    if (!line.startsWith("/") || line.length > 20 || line.contains(' ')) return null
    return lineStart to line.drop(1)
}

/** Les réglages de l'article, à droite de l'éditeur : 380 dp par défaut, de 300 à 560. */
private val EDITOR_PANE = org.teddapp.project.ui.PaneSpec("admin.editor", default = 380.dp, min = 300.dp, max = 560.dp)

@Composable
internal fun EditorScreen(model: AdminModel, state: EditorState, compact: Boolean) {
    val scope = androidx.compose.runtime.rememberCoroutineScope()
    // Un brouillon s'enregistre tout seul, deux secondes après la dernière frappe. Un
    // contenu en ligne attend le geste « Publier les modifications » : le site ne bouge
    // pas à chaque lettre.
    LaunchedEffect(state) {
        snapshotFlow { state.fingerprint() }.drop(1).debounce(2000).collect {
            if (!state.online && state.ready && state.dirty && !state.saving) model.saveEditor(publish = null)
        }
    }
    Column(Modifier.fillMaxSize()) {
        EditorTopBar(model, state, compact)
        Box(Modifier.fillMaxWidth().height(1.dp).background(TeddTokens.Line))
        if (compact) {
            Column(Modifier.fillMaxSize().verticalScroll(rememberScrollState())) {
                EditorMain(model, state, scope, compact = true, Modifier.padding(horizontal = TeddSpace.S4, vertical = TeddSpace.S5))
                Box(Modifier.fillMaxWidth().height(1.dp).background(TeddTokens.Line))
                EditorPanel(model, state, scope, Modifier.padding(horizontal = TeddSpace.S4, vertical = TeddSpace.S4))
            }
        } else {
            Row(Modifier.fillMaxSize()) {
                Box(Modifier.weight(1f).fillMaxHeight().verticalScroll(rememberScrollState()), contentAlignment = Alignment.TopCenter) {
                    EditorMain(model, state, scope, compact = false, Modifier.widthIn(max = 760.dp).padding(horizontal = TeddSpace.S6, vertical = TeddSpace.S8))
                }
                // Le filet est la poignée : on y règle la largeur du panneau de réglages.
                org.teddapp.project.ui.TeddSidePane(
                    spec = EDITOR_PANE, side = org.teddapp.project.ui.PaneSide.Right, title = "les réglages de l'article", icon = "settings",
                    contentPadding = androidx.compose.foundation.layout.PaddingValues(start = TeddSpace.S3, top = TeddSpace.S4, end = TeddSpace.S5, bottom = TeddSpace.S4),
                ) {
                    Box(Modifier.fillMaxSize().verticalScroll(rememberScrollState())) {
                        EditorPanel(model, state, scope, Modifier)
                    }
                }
            }
        }
    }
    if (state.confirmPublish) PublishDialog(model, state)
    if (state.confirmWithdraw) TeddConfirmDialog(
        title = "Retirer du site ?",
        message = "« ${state.title} » ne sera plus lisible sur le site, dans la minute. Le texte reste ici, en brouillon : tu peux le remettre en ligne quand tu veux.",
        confirmLabel = "Retirer du site",
        onConfirm = { state.confirmWithdraw = false; model.saveEditor(publish = false) },
        onDismiss = { state.confirmWithdraw = false },
        danger = false,
    )
    if (state.confirmDelete) TeddConfirmDialog(
        title = if (state.online) "Effacer ce contenu ?" else "Supprimer le brouillon ?",
        message = if (state.online) "« ${state.title} » sera retiré du site et effacé du serveur. C'est définitif." else "Le brouillon sera effacé du serveur. C'est définitif.",
        confirmLabel = "Effacer",
        onConfirm = { model.deleteEditor() },
        onDismiss = { state.confirmDelete = false },
    )
}

@Composable
private fun EditorTopBar(model: AdminModel, state: EditorState, compact: Boolean) {
    val rubric = when { state.isArticle -> "Blog"; state.isPiece -> "Portfolio"; state.role == "home" -> "Accueil"; state.role == "about" -> "À propos"; else -> "Autres pages" }
    Column(Modifier.fillMaxWidth().padding(horizontal = if (compact) TeddSpace.S3 else TeddSpace.S5, vertical = TeddSpace.S3), verticalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
            TeddButton(text = rubric, onClick = { model.closeEditor() }, kind = TeddButtonKind.Ghost, size = TeddButtonSize.Sm, icon = "arrow-left")
            if (!compact) {
                Text("/", color = TeddTokens.InkSubtle, style = TeddType.Caption)
                Text(state.title.ifBlank { "Sans titre" }, color = TeddTokens.InkMuted, style = TeddType.Caption, maxLines = 1, overflow = TextOverflow.Ellipsis, modifier = Modifier.widthIn(max = 260.dp))
            }
            if (state.online) TeddVisibilityBadge(TeddVisibility.Public, label = "En ligne") else TeddTag("Brouillon")
            Spacer(Modifier.weight(1f))
            if (!compact) LangSwitch(model, state)
            Spacer(Modifier.weight(1f))
            if (!compact) SavedIndicator(state)
            TeddButton(text = "Aperçu", onClick = { model.preview = state.previewDoc() }, kind = TeddButtonKind.Ghost, size = TeddButtonSize.Sm, icon = "eye")
            PublishButton(model, state)
            ProjectMoreMenu(
                buildList {
                    if (state.online) add(MenuAction("Retirer du site", "x", { state.confirmWithdraw = true }))
                    if (state.id != null) add(MenuAction(if (state.online) "Effacer du serveur" else "Supprimer le brouillon", "trash", { state.confirmDelete = true }, danger = true))
                },
            )
        }
        if (compact) Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S3)) {
            LangSwitch(model, state)
            Spacer(Modifier.weight(1f))
            SavedIndicator(state)
        }
        state.error?.let { FormError(it) }
    }
}

@Composable
private fun PublishButton(model: AdminModel, state: EditorState) {
    val label = when {
        !state.online -> "Publier en " + langName(state.lang)
        state.dirty -> "Publier les modifications"
        else -> "À jour sur le site"
    }
    TeddButton(
        text = if (state.saving) "Enregistrement…" else label,
        onClick = { state.confirmPublish = true },
        kind = TeddButtonKind.Publish,
        size = TeddButtonSize.Sm,
        icon = "globe",
        enabled = !state.saving && state.ready && (!state.online || state.dirty),
    )
}

/**
 * Le dialogue Publier : ce qui part sur le site, ce qui reste privé, l'adresse à copier,
 * les points à vérifier, puis la confirmation. On publie en connaissance de cause.
 */
@Composable
private fun PublishDialog(model: AdminModel, state: EditorState) {
    val clipboard = androidx.compose.ui.platform.LocalClipboardManager.current
    val update = state.online
    val what = when { state.isArticle -> "cet article"; state.isPiece -> "ce projet"; else -> "cette page" }
    val issues = state.checklist().filter { it.second == CheckState.Warn || it.second == CheckState.Missing }
    val url = model.siteUrl + state.path
    androidx.compose.ui.window.Dialog(onDismissRequest = { state.confirmPublish = false }, properties = androidx.compose.ui.window.DialogProperties(usePlatformDefaultWidth = false)) {
        Column(
            Modifier.widthIn(max = 580.dp).fillMaxWidth().padding(TeddSpace.S4).teddSurface(RoundedCornerShape(TeddTokens.RLg), elevation = 2.dp),
        ) {
            Column(Modifier.padding(start = TeddSpace.S6, end = TeddSpace.S6, top = TeddSpace.S6), verticalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                Text(("Publier · " + langName(state.lang)).uppercase(), color = TeddTokens.InkMuted, style = TeddType.Overline)
                Text(if (update) "Publier les modifications ?" else "Publier $what en ${langName(state.lang)} ?", color = TeddTokens.Ink, style = TeddType.Title1)
                Text(
                    if (update) "Le site reprendra ce texte à sa prochaine reconstruction, dans la minute. L'adresse ne change pas."
                    else "Il sera visible par tous sur le site. Tu pourras encore le modifier ici : chaque changement attendra une nouvelle publication.",
                    color = TeddTokens.InkMuted, style = TeddType.Body,
                )
            }
            Column(Modifier.padding(horizontal = TeddSpace.S6, vertical = TeddSpace.S5), verticalArrangement = Arrangement.spacedBy(TeddSpace.S4)) {
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                    TeddTag(if (update) "En ligne" else "Brouillon")
                    TeddIcon(name = "arrow-right", size = 14.dp, color = TeddTokens.InkMuted)
                    TeddVisibilityBadge(TeddVisibility.Public, label = if (update) "Mis à jour" else "Public")
                    Text("Sens unique : le site ne modifie jamais ce que tu écris ici.", color = TeddTokens.InkMuted, style = TeddType.Caption, maxLines = 2)
                }
                Row(
                    Modifier.fillMaxWidth().background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RMd)).padding(start = TeddSpace.S3, end = TeddSpace.S1, top = TeddSpace.S1, bottom = TeddSpace.S1),
                    verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2),
                ) {
                    TeddIcon(name = "globe", size = 14.dp, color = TeddTokens.Laterite)
                    Text(url.removePrefix("https://").removePrefix("http://"), color = TeddTokens.Ink, style = TeddType.MonoSm, maxLines = 1, overflow = TextOverflow.Ellipsis, modifier = Modifier.weight(1f))
                    TeddButton(text = "Copier", onClick = { clipboard.setText(androidx.compose.ui.text.AnnotatedString(url)); model.snackbar.show("Adresse copiée") }, kind = TeddButtonKind.Ghost, size = TeddButtonSize.Sm, icon = "copy")
                }
                Row(horizontalArrangement = Arrangement.spacedBy(TeddSpace.S4)) {
                    Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(TeddSpace.S1)) {
                        Text("PART SUR LE SITE", color = TeddTokens.InkMuted, style = TeddType.Overline)
                        Text(
                            when {
                                state.isArticle -> "Titre, chapeau et texte · couverture · tags et série · résumé · référencement"
                                state.isPiece -> "Titre, sous-titre et texte · période · liens · images · référencement"
                                else -> "Titre et texte · chiffres · " + (if (state.role == "home") "présentation de l'appli et bandeau" else if (state.role == "about") "contact" else "référencement")
                            },
                            color = TeddTokens.Ink, style = TeddType.Caption,
                        )
                    }
                    Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(TeddSpace.S1)) {
                        Text("RESTE PRIVÉ", color = TeddTokens.InkMuted, style = TeddType.Overline)
                        Text("Tes notes et tes pièces de l'appareil · la version " + (if (state.lang == "en") "française" else "anglaise") + " tant qu'elle n'est pas publiée", color = TeddTokens.InkMuted, style = TeddType.Caption)
                    }
                }
                if (issues.isNotEmpty()) Row(
                    Modifier.fillMaxWidth().background(TeddTokens.LateriteSoft, RoundedCornerShape(TeddTokens.RMd)).padding(TeddSpace.S3),
                    verticalAlignment = Alignment.Top, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2),
                ) {
                    TeddIcon(name = "alert", size = 16.dp, color = TeddTokens.Laterite)
                    Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(2.dp)) {
                        Text(
                            "${issues.size} point${if (issues.size > 1) "s" else ""} à vérifier. Tu peux publier quand même.",
                            color = TeddTokens.Ink, style = TeddType.BodyStrong,
                        )
                        issues.forEach { (label, check, hint) ->
                            Text("· " + label + (if (hint != null) " : $hint" else "") + if (check == CheckState.Missing) " (manque)" else "", color = TeddTokens.InkMuted, style = TeddType.Caption)
                        }
                    }
                }
            }
            Box(Modifier.fillMaxWidth().height(1.dp).background(TeddTokens.Line))
            Row(Modifier.fillMaxWidth().padding(horizontal = TeddSpace.S6, vertical = TeddSpace.S4), horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2, Alignment.End)) {
                TeddButton(text = "Annuler", onClick = { state.confirmPublish = false }, kind = TeddButtonKind.Ghost)
                TeddButton(
                    text = if (update) "Publier les modifications" else "Publier en " + langName(state.lang),
                    onClick = { state.confirmPublish = false; model.saveEditor(publish = true) },
                    kind = TeddButtonKind.Publish, icon = "globe",
                    enabled = state.ready && !state.saving && state.checklist().none { it.second == CheckState.Missing },
                )
            }
        }
    }
}

/** Français / English : la version qui existe s'ouvre, celle qui manque se crée en copiant celle-ci. */
@Composable
private fun LangSwitch(model: AdminModel, state: EditorState) {
    val other = if (state.lang == "en") "fr" else "en"
    val exists = model.otherVersion(state) != null
    TeddTabs(
        tabs = listOf(
            TeddTab(state.lang, if (state.lang == "en") "English" else "Français"),
            TeddTab(other, (if (other == "en") "English" else "Français") + if (exists) "" else " +"),
        ),
        selected = state.lang,
        onSelect = { if (it == other) model.switchLang(state) },
    )
}

@Composable
private fun SavedIndicator(state: EditorState) {
    val text = when {
        state.saving -> "Enregistrement…"
        state.id == null -> if (state.title.isBlank()) "Donne un titre : le brouillon s'enregistrera seul" else "Pas encore enregistré"
        state.online && state.dirty -> "Modifié · pas encore publié"
        state.dirty -> "Enregistrement dans un instant"
        else -> "Enregistré"
    }
    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S1)) {
        if (!state.saving && state.id != null && !state.dirty) TeddIcon(name = "check", size = 14.dp, color = TeddTokens.LimeStrong)
        Text(text, color = TeddTokens.InkMuted, style = TeddType.Caption, maxLines = 1)
    }
}

// ── La colonne de lecture ──────────────────────────────────────────────

@Composable
private fun EditorMain(model: AdminModel, state: EditorState, scope: kotlinx.coroutines.CoroutineScope, compact: Boolean, modifier: Modifier) {
    Column(modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(TeddSpace.S5)) {
        if (!state.isPage || state.role == "about") CoverBlock(model, state, scope)
        Text(
            buildList {
                add(when { state.isArticle -> "ARTICLE"; state.isPiece -> "PIÈCE DE PORTFOLIO"; else -> roleLabel(state.role).uppercase() })
                add("${state.words} MOT${if (state.words > 1) "S" else ""}")
                if (state.isArticle) add("${state.readingMinutes} MIN DE LECTURE")
            }.joinToString(" · "),
            color = TeddTokens.InkMuted, style = TeddType.Overline,
        )
        BigField(state.title, { state.title = it; if (!state.slugEdited) state.slug = slugify(it) }, placeholder = "Le titre", style = if (compact) TeddType.Title1 else TeddType.DisplayLg)
        if (!state.isPage) BigField(
            state.subtitle, { state.subtitle = it.take(160) },
            placeholder = if (state.isArticle) "Le chapeau : ce que l'article apporte, en une ou deux phrases." else "Un sous-titre : le projet en une ligne.",
            style = TeddType.BodyLg.copy(fontSize = 19.sp, lineHeight = 28.sp), color = TeddTokens.InkMuted,
        )
        Box(Modifier.fillMaxWidth().height(1.dp).background(TeddTokens.Line))
        BodyEditor(model, state, scope, compact)
    }
}

/** Un champ sans cadre, à la taille du titre : on écrit sur la page, pas dans un formulaire. */
@Composable
private fun BigField(value: String, onValueChange: (String) -> Unit, placeholder: String, style: androidx.compose.ui.text.TextStyle, color: androidx.compose.ui.graphics.Color = TeddTokens.Ink) {
    BasicTextField(
        value = value,
        onValueChange = { onValueChange(it.replace("\n", " ")) },
        textStyle = style.copy(color = color),
        cursorBrush = SolidColor(TeddTokens.Ink),
        modifier = Modifier.fillMaxWidth(),
        decorationBox = { inner ->
            if (value.isEmpty()) Text(placeholder, style = style, color = TeddTokens.InkSubtle)
            inner()
        },
    )
}

@Composable
private fun BodyEditor(model: AdminModel, state: EditorState, scope: kotlinx.coroutines.CoroutineScope, compact: Boolean) {
    val body = state.body
    val slash = slashQuery(body)
    Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
        NoteFormatBar(
            state = body,
            compact = compact,
            onImage = { scope.launch { model.pickImageInto(state, body) } },
            modifier = Modifier.background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RSm)).padding(vertical = 2.dp),
        )
        BasicTextField(
            state = body,
            inputTransformation = ListContinuation,
            textStyle = TeddType.BodyLg.copy(color = TeddTokens.Ink),
            cursorBrush = SolidColor(TeddTokens.Ink),
            modifier = Modifier.fillMaxWidth().heightIn(min = 320.dp).markdownShortcuts(body),
            decorator = { inner ->
                if (body.text.isEmpty()) Text(
                    "Écris ici. Tape « / » en début de ligne pour insérer un titre, une liste, une citation, du code, une image.",
                    style = TeddType.BodyLg, color = TeddTokens.InkSubtle,
                )
                inner()
            },
        )
        if (slash != null) SlashMenu(slash.second, onPick = { item ->
            body.format { t, s, _ ->
                val inserted = item.insert
                MdEdit(t.substring(0, slash.first) + inserted + t.substring(s), slash.first + inserted.length - item.cursorBack)
            }
        }, onImage = {
            body.format { t, s, _ -> MdEdit(t.substring(0, slash.first) + t.substring(s), slash.first) }
            scope.launch { model.pickImageInto(state, body) }
        }, onGallery = {
            body.format { t, s, _ -> MdEdit(t.substring(0, slash.first) + t.substring(s), slash.first) }
            model.galleryPicker = { ref -> body.format { t, s, _ -> MarkdownEdits.block(t, s, "![]($ref)") } }
        })
    }
}

/** Le menu « / » : les blocs qu'on insère au clavier, filtrés par ce qu'on tape après la barre. */
@Composable
private fun SlashMenu(query: String, onPick: (SlashItem) -> Unit, onImage: () -> Unit, onGallery: () -> Unit) {
    val q = query.lowercase()
    val items = SLASH_ITEMS.filter { q.isEmpty() || it.label.lowercase().contains(q) || it.code.lowercase().contains(q) }
    val image = q.isEmpty() || "image".contains(q) || "légende".contains(q)
    Column(Modifier.widthIn(max = 340.dp).teddSurface(RoundedCornerShape(TeddTokens.RMd)).padding(TeddSpace.S2), verticalArrangement = Arrangement.spacedBy(2.dp)) {
        Text("INSÉRER", color = TeddTokens.InkMuted, style = TeddType.Overline, modifier = Modifier.padding(horizontal = TeddSpace.S2, vertical = TeddSpace.S1))
        items.forEach { item -> SlashRow(item.code, item.label) { onPick(item) } }
        if (image) SlashRow("img", "Image depuis l'appareil", onImage)
        if (image) SlashRow("gal", "Image de la galerie", onGallery)
        if (items.isEmpty() && !image) Text("Rien ne répond à « $query »", color = TeddTokens.InkSubtle, style = TeddType.Caption, modifier = Modifier.padding(TeddSpace.S2))
    }
}

@Composable
private fun SlashRow(code: String, label: String, onClick: () -> Unit) {
    Row(
        modifier = Modifier.fillMaxWidth().teddClickable(onClick = onClick, shape = RoundedCornerShape(TeddTokens.RSm)).height(34.dp).padding(horizontal = TeddSpace.S2),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(TeddSpace.S3),
    ) {
        Box(Modifier.width(36.dp).background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RSm)).padding(vertical = 2.dp), contentAlignment = Alignment.Center) {
            Text(code, color = TeddTokens.InkMuted, style = TeddType.MonoSm.copy(fontSize = 11.sp), maxLines = 1)
        }
        Text(label, color = TeddTokens.Ink, style = TeddType.Body)
    }
}

/** La couverture : une image de l'appareil envoyée au serveur, ou une adresse web ; et ce qu'elle montre. */
@Composable
private fun CoverBlock(model: AdminModel, state: EditorState, scope: kotlinx.coroutines.CoroutineScope) {
    val shape = RoundedCornerShape(TeddTokens.RLg)
    var byUrl by remember(state) { mutableStateOf(false) }
    Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
        if (state.cover.isNotBlank()) {
            PreviewImage(state.cover, state.coverAlt, tall = true)
            Row(horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2), verticalAlignment = Alignment.CenterVertically) {
                TeddButton(text = "Remplacer", onClick = { scope.launch { model.pickCover(state) } }, size = TeddButtonSize.Sm, enabled = TeddImagePicker.pick != null)
                TeddButton(text = "Galerie", onClick = { model.galleryPicker = { ref -> state.cover = ref } }, size = TeddButtonSize.Sm, kind = TeddButtonKind.Ghost, icon = "image")
                TeddButton(text = "Retirer", onClick = { state.cover = ""; state.coverAlt = "" }, size = TeddButtonSize.Sm, kind = TeddButtonKind.Ghost)
                Spacer(Modifier.weight(1f))
                Text(if (state.cover.startsWith("file:")) "Image déposée sur le serveur" else state.cover, color = TeddTokens.InkSubtle, style = TeddType.MonoSm.copy(fontSize = 11.sp), maxLines = 1, overflow = TextOverflow.Ellipsis, modifier = Modifier.widthIn(max = 320.dp))
            }
            TeddField(label = "Ce que montre la couverture (texte alternatif)", value = state.coverAlt, onValueChange = { state.coverAlt = it.take(200) }, placeholder = "Un bureau avec un carnet ouvert")
        } else {
            Column(
                Modifier.fillMaxWidth().heightIn(min = 180.dp).clip(shape).background(TeddTokens.SurfaceSunken, shape).border(1.5.dp, TeddTokens.LineStrong, shape).padding(TeddSpace.S5),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(TeddSpace.S2, Alignment.CenterVertically),
            ) {
                TeddIcon(name = "image", size = 28.dp, color = TeddTokens.InkMuted)
                Text("Ajouter une image de couverture", color = TeddTokens.Ink, style = TeddType.BodyStrong)
                Text("JPG, PNG ou WebP, 6 Mo au plus · 1600 × 900 conseillé", color = TeddTokens.InkMuted, style = TeddType.Caption)
                Row(horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                    TeddButton(text = "Choisir un fichier", onClick = { scope.launch { model.pickCover(state) } }, size = TeddButtonSize.Sm, enabled = TeddImagePicker.pick != null)
                    TeddButton(text = "Depuis la galerie", onClick = { model.galleryPicker = { ref -> state.cover = ref } }, size = TeddButtonSize.Sm, kind = TeddButtonKind.Secondary, icon = "image")
                    TeddButton(text = "Une adresse web", onClick = { byUrl = !byUrl }, size = TeddButtonSize.Sm, kind = TeddButtonKind.Ghost)
                }
                if (TeddImagePicker.pick == null) Text("Cet appareil ne sait pas choisir une image : passe par une adresse web, ou par l'ordinateur.", color = TeddTokens.InkSubtle, style = TeddType.Caption)
            }
            if (byUrl) {
                var url by remember { mutableStateOf("") }
                Row(verticalAlignment = Alignment.Bottom, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                    TeddField(label = "Adresse de l'image", value = url, onValueChange = { url = it.trim() }, placeholder = "https://…", modifier = Modifier.weight(1f))
                    TeddButton(text = "Utiliser", onClick = { state.cover = url; byUrl = false }, enabled = url.startsWith("http"))
                }
            }
        }
    }
}

// ── Le panneau ─────────────────────────────────────────────────────────

@Composable
private fun PanelSection(title: String, trailing: (@Composable () -> Unit)? = null, content: @Composable () -> Unit) {
    Column(Modifier.fillMaxWidth().padding(vertical = TeddSpace.S4), verticalArrangement = Arrangement.spacedBy(TeddSpace.S3)) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Text(title, color = TeddTokens.Ink, style = TeddType.Title2, modifier = Modifier.weight(1f))
            trailing?.invoke()
        }
        content()
    }
    Box(Modifier.fillMaxWidth().height(1.dp).background(TeddTokens.Line))
}

@Composable
private fun EditorPanel(model: AdminModel, state: EditorState, scope: kotlinx.coroutines.CoroutineScope, modifier: Modifier) {
    Column(modifier.fillMaxWidth()) {
        PanelSection("Publication", trailing = { if (state.online) TeddVisibilityBadge(TeddVisibility.Public, label = "En ligne") else TeddTag("Brouillon") }) {
            if (state.role == null) TeddField(
                label = if (state.isArticle) "Adresse de l'article" else if (state.isPiece) "Adresse du projet" else "Adresse de la page",
                value = state.slug, onValueChange = { state.slug = slugify(it, typing = true); state.slugEdited = true }, placeholder = "mon-article",
            )
            Text(model.siteUrl.removePrefix("https://").removePrefix("http://") + state.path, color = TeddTokens.InkMuted, style = TeddType.MonoSm.copy(fontSize = 11.sp), maxLines = 1, overflow = TextOverflow.Ellipsis)
            if (state.translationOf != null) Hint("Reliée à la version ${langName(if (state.lang == "en") "fr" else "en")}e : le site proposera de passer de l'une à l'autre.")
        }
        PanelSection("Classement") {
            when {
                state.isArticle -> ArticleClassification(state)
                state.isPiece -> PieceClassification(model, state, scope)
                else -> PageClassification(state)
            }
        }
        PanelSection("Version " + if (state.lang == "en") "française" else "anglaise", trailing = { TeddTag(if (state.lang == "en") "FR" else "EN") }) {
            val other = model.otherVersion(state)
            if (other != null) {
                Text("« ${other.title} »", color = TeddTokens.Ink, style = TeddType.Body, maxLines = 2, overflow = TextOverflow.Ellipsis)
                Row(horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                    TeddButton(text = "Ouvrir", onClick = { model.switchLang(state) }, size = TeddButtonSize.Sm, icon = "edit")
                    if (other.online) TeddVisibilityBadge(TeddVisibility.Public, label = "En ligne") else TeddTag("Brouillon")
                }
            } else {
                Hint("Pas encore créée. Le titre, le chapeau et le texte sont copiés pour être traduits ; les deux versions restent liées et se publient séparément.")
                TeddButton(text = "Créer la version " + if (state.lang == "en") "française" else "anglaise", onClick = { model.switchLang(state) }, size = TeddButtonSize.Sm, icon = "plus", enabled = state.id != null)
                if (state.id == null) Hint("Enregistre d'abord ce brouillon : un titre suffit.")
            }
        }
        PanelSection("Référencement") {
            TeddField(label = "Titre pour les moteurs", value = state.seoTitle, onValueChange = { state.seoTitle = it.take(70) }, placeholder = state.title.ifBlank { "Le titre, sinon" }, trailingContent = { Counter(state.seoTitle.length, 70) })
            Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S1)) {
                FormLabel("Description")
                TextArea(state.seoDescription, { state.seoDescription = it.take(200) }, placeholder = if (state.summary.isNotBlank()) "Le résumé, sinon" else "Deux phrases qui donnent envie de cliquer.", minHeight = 72.dp)
                Counter(state.seoDescription.length, 200)
            }
            SeoPreview(model, state)
        }
        PanelSection("Avant de publier") {
            state.checklist().forEach { (label, check, hint) ->
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                    val (icon, color) = when (check) {
                        CheckState.Done -> "check" to TeddTokens.LimeStrong
                        CheckState.Warn -> "alert" to TeddTokens.Laterite
                        CheckState.Missing -> "x" to TeddTokens.Danger
                        CheckState.Optional -> "clock" to TeddTokens.InkSubtle
                    }
                    TeddIcon(name = icon, size = 16.dp, color = color)
                    Text(label, color = if (check == CheckState.Optional) TeddTokens.InkMuted else TeddTokens.Ink, style = TeddType.Body, modifier = Modifier.weight(1f))
                    if (hint != null) Text(hint, color = TeddTokens.InkMuted, style = TeddType.Caption)
                }
            }
        }
        if (state.id != null) Column(Modifier.padding(vertical = TeddSpace.S4)) {
            TeddButton(
                text = if (state.online) "Effacer du serveur" else "Supprimer le brouillon",
                onClick = { state.confirmDelete = true }, kind = TeddButtonKind.Ghost, size = TeddButtonSize.Sm, icon = "trash",
            )
        }
    }
}

@Composable
private fun Counter(count: Int, max: Int) {
    Text("$count/$max", color = if (count > max * 9 / 10) TeddTokens.Laterite else TeddTokens.InkSubtle, style = TeddType.MonoSm.copy(fontSize = 11.sp))
}

/** Le résultat tel que Google le montrerait : adresse, titre, description. */
@Composable
private fun SeoPreview(model: AdminModel, state: EditorState) {
    val host = model.siteUrl.removePrefix("https://").removePrefix("http://")
    val crumbs = host + " › " + state.path.trim('/').split('/').joinToString(" › ")
    Column(Modifier.fillMaxWidth().background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RMd)).padding(TeddSpace.S4), verticalArrangement = Arrangement.spacedBy(2.dp)) {
        Text(crumbs, color = TeddTokens.InkMuted, style = TeddType.MonoSm.copy(fontSize = 11.sp), maxLines = 1, overflow = TextOverflow.Ellipsis)
        Text(state.seoTitle.ifBlank { state.title }.ifBlank { "Le titre" }, color = TeddTokens.Link, style = TeddType.BodyStrong.copy(fontSize = 17.sp), maxLines = 1, overflow = TextOverflow.Ellipsis)
        Text(
            state.seoDescription.ifBlank { state.summary }.ifBlank { state.subtitle }.ifBlank { "La description" },
            color = TeddTokens.InkMuted, style = TeddType.Caption, maxLines = 3, overflow = TextOverflow.Ellipsis,
        )
    }
}

@Composable
private fun ArticleClassification(state: EditorState) {
    Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S3)) {
        Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S1)) {
            FormLabel("Résumé pour le catalogue")
            TextArea(state.summary, { state.summary = it.take(400) }, placeholder = "Deux ou trois phrases : de quoi parle l'article, et pour qui.", minHeight = 80.dp)
        }
        TeddField(label = "Tags, séparés par des virgules", value = state.tags, onValueChange = { state.tags = it }, placeholder = "offline-first, synchronisation")
        val options = buildList<Pair<String?, String>> {
            add(null to "Aucune")
            state.known.forEach { s -> add(s.key to (s.titles[state.lang] ?: s.titles.values.first())) }
            // La série de cet article, si la liste des séries ne la connaît pas encore.
            state.seriesKey?.takeIf { k -> k != ArticleState.NEW_SERIES && state.known.none { it.key == k } }?.let { add(it to state.seriesTitle.ifBlank { it }) }
            add(ArticleState.NEW_SERIES to "Nouvelle série")
        }
        Choice(
            label = "Série",
            options = options,
            selected = state.seriesKey,
            onSelect = { key ->
                state.seriesKey = key
                val known = state.known.firstOrNull { it.key == key }
                state.seriesTitle = known?.titles?.get(state.lang).orEmpty()
                state.episode = if (key == null) "" else ((known?.lastEpisode?.get(state.lang) ?: 0) + 1).toString()
            },
        )
        if (state.seriesKey != null) Row(horizontalArrangement = Arrangement.spacedBy(TeddSpace.S3)) {
            TeddField(label = "Titre de la série", value = state.seriesTitle, onValueChange = { state.seriesTitle = it.take(120) }, placeholder = "Construire une synchronisation", modifier = Modifier.weight(1f))
            TeddField(label = "Épisode", value = state.episode, onValueChange = { v -> state.episode = v.filter(Char::isDigit).take(2) }, keyboardType = androidx.compose.ui.text.input.KeyboardType.Number, placeholder = "1", modifier = Modifier.width(80.dp))
        }
    }
}

@Composable
private fun PieceClassification(model: AdminModel, state: EditorState, scope: kotlinx.coroutines.CoroutineScope) {
    Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S3)) {
        Row(horizontalArrangement = Arrangement.spacedBy(TeddSpace.S3)) {
            TeddField(label = "Début", value = state.dateStart, onValueChange = { state.dateStart = it.filter { c -> c.isDigit() || c == '-' }.take(10) }, placeholder = "2026-01", modifier = Modifier.weight(1f))
            TeddField(label = "Fin", value = state.dateEnd, onValueChange = { state.dateEnd = it.filter { c -> c.isDigit() || c == '-' }.take(10) }, placeholder = "2026-06", modifier = Modifier.weight(1f))
        }
        Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S1)) {
            FormLabel("Liens")
            Hint("Un par ligne, huit au plus : le libellé, une barre, puis l'adresse.")
            TextArea(state.links, { state.links = it }, placeholder = "Dépôt | https://github.com/moi/projet\nDémo | https://projet.exemple.org", minHeight = 72.dp)
        }
        Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                FormLabel("Images")
                Spacer(Modifier.weight(1f))
                TeddButton(text = "Galerie", onClick = { model.galleryPicker = { ref -> if (state.images.size < 12) state.images.add(ref to "") } }, size = TeddButtonSize.Sm, kind = TeddButtonKind.Ghost, icon = "image", enabled = state.images.size < 12)
                TeddButton(text = "Fichier", onClick = { scope.launch { model.pickPieceImage(state) } }, size = TeddButtonSize.Sm, kind = TeddButtonKind.Ghost, icon = "plus", enabled = TeddImagePicker.pick != null && state.images.size < 12)
            }
            if (state.images.isEmpty()) Hint("Les images du projet, dans l'ordre où le site les montre. Douze au plus.")
            state.images.forEachIndexed { index, (url, caption) ->
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                    Box(Modifier.width(56.dp).height(40.dp)) { PreviewImage(url, caption, tall = false) }
                    TeddField(label = "Légende", value = caption, onValueChange = { state.images[index] = url to it.take(200) }, modifier = Modifier.weight(1f))
                    ProjectMoreMenu(
                        listOfNotNull(
                            if (index > 0) MenuAction("Monter", "arrow-up", { state.images.add(index - 1, state.images.removeAt(index)) }) else null,
                            if (index < state.images.lastIndex) MenuAction("Descendre", "arrow-down", { state.images.add(index + 1, state.images.removeAt(index)) }) else null,
                            MenuAction("Retirer", "trash", { state.images.removeAt(index) }, danger = true),
                        ),
                    )
                }
            }
        }
    }
}

@Composable
private fun PageClassification(state: EditorState) {
    Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S3)) {
        Text(roleLabel(state.role), color = TeddTokens.InkMuted, style = TeddType.Caption)
        if (state.role == "home" || state.role == "about") Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S1)) {
            FormLabel("Chiffres")
            Hint(if (state.role == "about") "Un par ligne, quatre au plus : le chiffre, une barre, ce qu'il compte. Sans chiffres, la page reprend ceux de l'accueil." else "Un par ligne, quatre au plus. Exemple : 8 ans | d'expérience")
            TextArea(state.stats, { state.stats = it }, placeholder = "8 ans | d'expérience\n20 | projets livrés", minHeight = 80.dp)
        }
        if (state.role == "home") HomeFields(state.home)
        if (state.role == "about") AboutFields(state.about)
    }
}
